import React, { useEffect, useRef, useState } from "react";
import { CSSTransition, TransitionGroup } from "react-transition-group";
import GuiPopupBubbleStackView, { Bubble } from "./GuiPopupBubbleView";


type GuiPopupAnimatedViewProps = {
  stackId: number;
  bubbles: Bubble[];
  durationSec: number;
  onElapsed?: (stackId: number) => void;
  fadeMs?: number;
};

const GuiPopupAnimatedView: React.FC<GuiPopupAnimatedViewProps> = ({
  stackId,
  bubbles,
  durationSec,
  onElapsed,
  fadeMs = 300,
}) => {
  // Generations: keep old + new briefly to animate between them
  const genCounterRef = useRef(0);
  const [generations, setGenerations] = useState<
    Array<{ key: string; bubbles: Bubble[] }>
  >([]);

  // A ref-per-generation for CSSTransition.nodeRef
  const nodeRefs = useRef<Map<string, React.RefObject<HTMLDivElement>>>(new Map());
  const ensureNodeRef = (key: string) => {
    let ref = nodeRefs.current.get(key);
    if (!ref) {
      ref = React.createRef<HTMLDivElement>();
      nodeRefs.current.set(key, ref);
    }
    return ref;
  };

  // Lifespan timer
  const lifeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // On bubbles change: stage a new generation and schedule removal of the old one
  useEffect(() => {
    const gen = String(++genCounterRef.current);
    setGenerations((prev) => [...prev, { key: gen, bubbles: bubbles ?? [] }]);

    // Remove all previous generations on next tick (triggers exit for them)
    const cleanupTick = setTimeout(() => {
      setGenerations((curr) => curr.filter((g) => g.key === gen));
    }, 0);

    // (Re)start lifespan timer
    if (lifeTimerRef.current) clearTimeout(lifeTimerRef.current);
    lifeTimerRef.current = setTimeout(() => {
      onElapsed?.(stackId);
    }, Math.max(0, Math.round(durationSec * 1000)));

    return () => clearTimeout(cleanupTick);
  }, [bubbles, durationSec, stackId]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (lifeTimerRef.current) clearTimeout(lifeTimerRef.current);
    };
  }, []);

  return (
    <>
      {/* Scoped transition styles */}
      <style>{`
        .gpav-layer {
          position: absolute;
          inset: 0;
          pointer-events: none; /* lets clicks pass through in demos */
        }

        /* Appear + Enter: slide up from below, fade in */
        .gpav-appear,
        .gpav-enter {
          opacity: 0;
          transform: translateY(60px);
        }
        .gpav-appear-active,
        .gpav-enter-active {
          opacity: 1;
          transform: translateY(0);
          transition: opacity ${fadeMs}ms ease, transform ${fadeMs}ms ease;
        }

        /* Exit: nudge up and fade out */
        .gpav-exit {
          opacity: 1;
          transform: translateY(0);
        }
        .gpav-exit-active {
          opacity: 0;
          transform: translateY(-60px);
          transition: opacity ${Math.max(150, Math.round(fadeMs * 0.8))}ms ease,
                      transform ${Math.max(150, Math.round(fadeMs * 0.8))}ms ease;
        }
      `}</style>

      {/* Relative wrapper so layered generations stack correctly */}
      <div style={{ position: "relative" }}>
        <TransitionGroup component={null}>
          {generations.map((g) => {
            const nodeRef = ensureNodeRef(g.key);
            return (
              <CSSTransition
                key={g.key}
                nodeRef={nodeRef}
                timeout={fadeMs}
                classNames="gpav"
                appear
              >
                <GuiPopupBubbleStackView 
                  ref={nodeRef}
                  bubbles={g.bubbles}
                  className="gpav-target" 
                />
              </CSSTransition>
            );
          })}
        </TransitionGroup>
      </div>
    </>
  );
};

export default GuiPopupAnimatedView;